import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { programsAuthedQuery } from "@/lib/queries";
import { adminSaveProgram } from "@/lib/admin.functions";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/_authenticated/t1dwpanel/programs")({
  head: () => ({
    meta: [
      { title: "Programs | Warrior Volunteer" },
      { name: "description", content: "Programs in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Programs | Warrior Volunteer" },
      { property: "og:description", content: "Programs in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProgramsPage,
});

function ProgramsPage() {
  const { data: programs = [] } = useQuery(programsAuthedQuery);
  const save = useServerFn(adminSaveProgram);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function handleSave(event: React.FormEvent<HTMLFormElement>, id?: string) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      await save({
        data: {
          id,
          slug: String(form.get("slug") ?? ""),
          name: String(form.get("name") ?? ""),
          summary: String(form.get("summary") ?? ""),
          site_url: String(form.get("site_url") ?? ""),
          sort_order: Number(form.get("sort_order") ?? 0),
          is_active: form.get("is_active") === "on",
        },
      });
      toast.success(id ? "Program updated." : "Program created.");
      if (!id) formEl.reset();
      queryClient.invalidateQueries({ queryKey: ["programs"] });
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "handleSave" });
      toast.error(error instanceof Error ? error.message : "Could not save the program.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell eyebrow="Admin" title="Programs">
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <form onSubmit={(e) => handleSave(e)} className="panel h-fit space-y-4 p-6">
          <h2 className="t-h3 text-ice">New program</h2>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" name="name" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input id="slug" name="slug" required placeholder="in-person-event-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea id="summary" name="summary" rows={3} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="site_url">Program page URL</Label>
            <Input id="site_url" name="site_url" placeholder="https://t1dw.org/..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sort_order">Sort order</Label>
            <Input id="sort_order" name="sort_order" type="number" defaultValue={10} />
          </div>
          <label className="flex items-center gap-2 text-sm text-ice/85">
            <input type="checkbox" name="is_active" defaultChecked /> Active
          </label>
          <Button type="submit" disabled={busy} className="w-full">
            Create program
          </Button>
        </form>

        <div className="space-y-4">
          {programs.map((program) => (
            <form
              key={program.id}
              onSubmit={(e) => handleSave(e, program.id)}
              className="panel space-y-4 p-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input name="name" defaultValue={program.name} />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input name="slug" defaultValue={program.slug} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Summary</Label>
                <Textarea name="summary" rows={2} defaultValue={program.summary} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Program page URL</Label>
                  <Input name="site_url" defaultValue={program.site_url ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Sort order</Label>
                  <Input name="sort_order" type="number" defaultValue={program.sort_order} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-ice/85">
                  <input type="checkbox" name="is_active" defaultChecked={program.is_active} />{" "}
                  Active
                </label>
                <Button type="submit" size="sm" disabled={busy} >
                  Save
                </Button>
              </div>
            </form>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
